import { 
  CheckCircle2, 
  ArrowRight, 
  Users, 
  FileText, 
  BarChart3,
  Zap,
  Shield,
  Clock
} from "lucide-react";

const delegationFramework = [
  {
    phase: "Phase 1: Foundation",
    timeline: "Seed - Series A",
    tasks: [
      {
        title: "Document Everything",
        description: "Before you can delegate, you need processes. Write SOPs for recurring tasks.",
        icon: FileText
      },
      {
        title: "Identify Your Unique Value",
        description: "What can ONLY you do? Vision, fundraising, key relationships. Everything else is delegatable.",
        icon: Zap
      },
      {
        title: "Hire for Ownership",
        description: "Look for people who've owned outcomes before, not just executed tasks.",
        icon: Users
      }
    ]
  },
  {
    phase: "Phase 2: Transfer",
    timeline: "Series A - B",
    tasks: [
      {
        title: "Delegate Decisions, Not Just Tasks",
        description: "Give authority with responsibility. 'Own the outcome' not 'do what I say.'",
        icon: Shield
      },
      {
        title: "Set Metrics, Not Methods",
        description: "Define success criteria and let the team figure out how to achieve them.",
        icon: BarChart3
      },
      {
        title: "Create Escalation Paths",
        description: "When should they come to you? Define clear thresholds for decisions that need founder input.",
        icon: ArrowRight
      }
    ]
  },
  {
    phase: "Phase 3: Scale",
    timeline: "Series B+",
    tasks: [
      {
        title: "Build Manager Layer",
        description: "Your direct reports should be delegating too. Teach them how.",
        icon: Users
      },
      {
        title: "Weekly Sync, Not Daily Check-ins",
        description: "Move from micromanagement to accountability cadences.",
        icon: Clock
      },
      {
        title: "Celebrate Autonomous Wins",
        description: "Publicly recognize when teams achieve outcomes without your involvement.",
        icon: CheckCircle2
      }
    ]
  }
];

export const StartupFailureDelegationGuide = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 id="delegation-framework" className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-primary" />
        The Founder Delegation Framework
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        A phase-by-phase guide to building a team that can scale without becoming dependent on you for every decision.
      </p>

      <div className="grid gap-8">
        {delegationFramework.map((phase, phaseIndex) => (
          <div key={phase.phase} className="relative">
            {/* Phase Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                {phaseIndex + 1}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{phase.phase}</h3>
                <p className="text-sm text-muted-foreground">{phase.timeline}</p>
              </div>
            </div>

            {/* Phase Tasks */}
            <div className="grid sm:grid-cols-3 gap-4 ml-0 sm:ml-14">
              {phase.tasks.map((task) => (
                <div 
                  key={task.title}
                  className="bg-card border border-border rounded-lg p-5 hover:border-primary/30 transition-colors"
                >
                  <div className="p-2 bg-primary/10 rounded-lg w-fit mb-3">
                    <task.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">{task.title}</h4>
                  <p className="text-sm text-muted-foreground">{task.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
